import { MAX_DESIGN_NAME_LENGTH } from '@repo/api-contracts';
import { Pencil } from 'lucide-react';
import { type KeyboardEvent, type ReactElement, useCallback, useRef, useState } from 'react';
import { getErrorMessage } from '@/shared/lib';
import { useRenameMyDesign } from '../model/use-rename-my-design';

const RENAME_FALLBACK = '이름을 바꾸지 못했어요. 잠시 후 다시 시도해 주세요.';

interface MyDesignNameEditorProps {
  readonly design: { readonly id: string; readonly name: string };
}

/**
 * 카드 이름 자리의 인라인 편집기 — 이름(연필 표시)을 누르면 입력칸으로 바뀌고, Enter·포커스 이탈로 저장, Esc 로 취소한다.
 * 비우거나 그대로 두면 요청 없이 돌아간다. 실패하면 입력칸을 열어 둔 채 아래에 사유를 보인다.
 */
export const MyDesignNameEditor = ({ design }: MyDesignNameEditorProps): ReactElement => {
  const { rename, isPending } = useRenameMyDesign();
  // null 이면 보기 모드. 문자열이면 편집 중인 값.
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Enter 로 저장해 입력칸이 사라질 때 브라우저가 blur 를 한 번 더 보내도 두 번 저장하지 않게.
  const settledRef = useRef(false);

  const focusOnMount = useCallback((node: HTMLInputElement | null) => {
    node?.focus();
    node?.select();
  }, []);

  const close = (): void => {
    settledRef.current = true;
    setDraft(null);
    setError(null);
  };

  const commit = async (): Promise<void> => {
    if (draft === null || settledRef.current || isPending) return;
    const next = draft.trim();
    if (next.length === 0 || next === design.name) {
      close();
      return;
    }
    setError(null);
    try {
      await rename(design.id, next);
      close();
    } catch (caught) {
      setError(getErrorMessage(caught, RENAME_FALLBACK));
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  if (draft === null) {
    return (
      <button
        type="button"
        aria-label={`${design.name} 이름 바꾸기`}
        title="이름 바꾸기"
        className="group flex max-w-full min-w-0 cursor-text items-start gap-1.5 text-left"
        onClick={() => {
          settledRef.current = false;
          setError(null);
          setDraft(design.name);
        }}
      >
        <span className="min-w-0 wrap-anywhere">{design.name}</span>
        <Pencil
          aria-hidden
          strokeWidth={1.5}
          className="mt-[3px] size-3.5 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-foreground"
        />
      </button>
    );
  }

  return (
    <span className="flex flex-col gap-1">
      <input
        ref={focusOnMount}
        aria-label="디자인 이름"
        value={draft}
        maxLength={MAX_DESIGN_NAME_LENGTH}
        disabled={isPending}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => void commit()}
        className="h-8 w-full rounded-none border border-foreground bg-background px-2 text-[15px] font-semibold outline-none disabled:opacity-50"
      />
      {error ? (
        <span role="alert" className="text-xs font-normal text-destructive">
          {error}
        </span>
      ) : null}
    </span>
  );
};
